/* eslint-disable max-len */
/* eslint-disable no-shadow */
import { debounce } from "throttle-debounce";
// import { scaleLinear } from 'd3-scale';
import { getLanguage } from "./language";
import { descending, loadScript } from "../utils";
import { MapPopup } from "./map-popup";
import { convertToGeoJSON } from "./mapbox_helpers";
import { Dispatcher } from "./dispatcher";
import "./scss/_map__base.scss";
import "./scss/_map__popup.scss";
import "./scss/_map__tooltips.scss";
import "./scss/_map__circle_markers.scss";

const DEBUG = true;

let mapboxgl;

const template = `
  <div class="tsg-map">
    <div class="tsg-map__map"></div>
    <div class="tsg-map__controls"></div>
    <div class="tsg-map__legend"></div>
    <div class="tsg-map__popup-container"></div>
  </div>
`;

const stylePastel =
	"https://api.maptiler.com/maps/d9431abd-4f0b-4805-9179-f250aca98a37/style.json?key=xyX8aavHilZMC9EUtX3J";
const styleSimple = "https://interaktiv.tagesspiegel.de/arenahousing/simpletiles/style.json";
const styleBW =
	"https://api.maptiler.com/maps/8813b5c3-1723-4a23-9aed-180e1c1f99dc/?key=xyX8aavHilZMC9EUtX3J#10.6/52.5003/13.4044";
const styleWahl =
	"https://api.maptiler.com/maps/8813b5c3-1723-4a23-9aed-180e1c1f99dc/style.json?key=xyX8aavHilZMC9EUtX3J";
const styleWahlDe =
	"https://api.maptiler.com/maps/fc6dfb85-835a-4da1-b164-b1b8342baed2/style.json?key=xyX8aavHilZMC9EUtX3J";
const warMap = "https://api.maptiler.com/maps/8067f98c-37ab-4c2d-9db1-105d3eed7cee/style.json?key=xyX8aavHilZMC9EUtX3J";
const warMapV2 =
	"https://api.maptiler.com/maps/61c65d25-adda-46e2-83a9-c86153203ea0/style.json?key=xyX8aavHilZMC9EUtX3J";
const warMapV3 =
	"https://api.maptiler.com/maps/5705e730-52d1-43b8-b1af-68fc82104f13/style.json?key=xyX8aavHilZMC9EUtX3J";
const warMapDev =
	"https://api.maptiler.com/maps/d0b8a817-007b-47d9-858d-6b06eefd3253/style.json?key=Q08s7QLW69RVwWAg9AaA";
const pipelineMap =
	"https://api.maptiler.com/maps/5c73a149-2362-485b-9dd0-bca543f868f3/style.json?key=xyX8aavHilZMC9EUtX3J";

class Map extends Dispatcher {
	constructor(container, options) {
		super();
		mapboxgl = window.mapboxgl;
		this.container = container;
		this.options = options;
		this.choropleths = {};
		this.lines = {};
		this.labels = {};
		this.setupHTML();

		// Only load map when close to viewport (to save $$$$ on tiles);
		this.activated = new Promise((resolve) => {
			const observer = new IntersectionObserver(
				(entries) => {
					if (entries[0].intersectionRatio > 0) {
						this.setupMap();
						observer.unobserve(this.elems.main);
						resolve();
					}
				},
				{ threshold: [0], rootMargin: "600px" }
			);
			observer.observe(this.elems.main);
		});

		// check whether the device is a touch device
		const isTouchDevice = () => {
			return "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
		};
		this.isTouch = isTouchDevice();
		this.elems.main.classList.toggle("tsg-map--is-touch", this.isTouch);

		if (this.options.controls) this.setupControls();
		if (this.options.popup) this.setupPopup();
		this.setupResize();
	}

	getStyle(style) {
		if (!style || style === "default" || style === "pastel") {
			this.provider = "maptiler";
			return stylePastel;
		}
		if (style === "simple") {
			this.provider = "self";
			return styleSimple;
		}
		if (style === "bw") {
			this.provider = "self";
			return styleBW;
		}
		if (style === "wahl") {
			this.provider = "maptiler";
			return styleWahl;
		}
		if (style === "wahl_de") {
			this.provider = "maptiler";
			return styleWahlDe;
		}
		if (style === "warMap") {
			this.provider = "maptiler";
			return warMap;
		}
		if (style === "warMapV2") {
			this.provider = "maptiler";
			return warMapV2;
		}
		if (style === "warMapV3") {
			this.provider = "maptiler";
			return warMapV3;
		}
		if (style === "warMapDev") {
			this.provider = "maptiler";
			return warMapDev;
		}
		if (style === "pipelineMap") {
			this.provider = "maptiler";
			return pipelineMap;
		}
		/*
    if (style === 'mb') {
      this.provider = 'mapbox';
      mapboxgl.accessToken = mbAccessToken;
      return mbStyle;
    }
    */
		return style;
	}

	setupHTML() {
		this.container.innerHTML = template;
		this.elems = {
			main: this.container.querySelector(".tsg-map"),
			map: this.container.querySelector(".tsg-map__map"),
			legend: this.container.querySelector(".tsg-map__legend"),
			controls: this.container.querySelector(".tsg-map__controls"),
			popupContainer: this.container.querySelector(".tsg-map__popup-container"),
		};
	}

	setupResize() {
		const r = () => {
			this.width = this.elems.main.getBoundingClientRect().width;

			if (this.options.controlsBreakpointMin) {
				this.elems.main.classList.toggle("tsg-map--hide-controls", this.width < this.options.controlsBreakpointMin);
			}

			if (this.options.controlsBreakpointMax) {
				this.elems.main.classList.toggle("tsg-map--hide-controls", this.width > this.options.controlsBreakpointMax);
			}

			if (this.options.largeCircleMarkersBreakpoint) {
				this.elems.main.classList.toggle(
					"tsg-map--large-circle-markers",
					this.width >= this.options.largeCircleMarkersBreakpoint
				);
			}

			if (this.options.style === "simple") {
				this.elems.main.classList.add("tsg-map--no-attribution");
			} else {
				this.elems.main.classList.remove("tsg-map--no-attribution");
			}
			if (this.width <= 640) {
				this.elems.main.parentNode.parentNode.classList.add("tsg-map-mobile");
			} else {
				this.elems.main.parentNode.parentNode.classList.remove("tsg-map-mobile");
			}

			if (this.popup && this.popup.isMobile) {
				this.elems.main.classList.add("tsg-map--popup-mobile");
			} else {
				this.elems.main.classList.remove("tsg-map--popup-mobile");
			}

			this.dispatch("resize");
		};

		r();

		let ww = window.innerWidth;
		window.addEventListener(
			"resize",
			debounce(200, () => {
				if (ww !== window.innerWidth) {
					ww = window.innerWidth;
					r();
				}
			})
		);
	}

	setupMap() {
		// create map
		this.map = new mapboxgl.Map({
			container: this.elems.map,
			style: this.options.styleJson || this.getStyle(this.options.style),
			minZoom: this.options.minZoom,
			maxZoom: this.options.maxZoom,
			maxBounds: this.options.maxBounds,
			attributionControl: false,
			pitchWithRotate: false,
			dragRotate: false,
			touchZoomRotate: false,
			touchPitch: false,
		});
		this.map.touchZoomRotate.disableRotation();
		this.attributionControl = new mapboxgl.AttributionControl({
			compact: false,
		});
		this.map.addControl(this.attributionControl);

		if (DEBUG) window.map = this;

		// this.map.on('load', function () {
		//   this.map.resize();
		// });

		// store a loading promise that can be used whenver the stylesheet
		// needs to be loaded already (e.g. when one needs access to the layers)
		this.loaded = new Promise((resolve) => {
			this.map.once("styledata", () => {
				resolve();
			});
		});

		// store the ids of label layers (for making it possible to hide them)
		this.loaded.then(() => {
			this.labelLayerIds = this.map
				.getStyle()
				.layers.filter((x) => x.type === "symbol" && x.layout["text-field"])
				.filter((x) => x.visibility !== "none") // attention: we only save visible layers
				.map((x) => x.id);
			this.firstLabelLayer = this.labelLayerIds[0];
			if (this.getStyle(this.options.style) === stylePastel && !this.options.styleJson) {
				this.firstLabelLayer = "airport-label";
			} // HOTFIX
			this.symbolLayerIds = this.map
				.getStyle()
				.layers.filter((x) => x.type === "symbol")
				.map((x) => x.id);
		});

		// patch fonts
		if (this.options.useFranklin) {
			this.loaded.then(() => {
				this.map.style.glyphManager.url = "https://interaktiv.tagesspiegel.de/maptiles/fonts/{fontstack}/{range}.pbf";
				this.symbolLayerIds.forEach((lid) => {
					const lp = this.map.getLayoutProperty(lid, "text-font");
					const tf = lp ? lp[0] : "";
					const italic = tf.indexOf("Italic") !== -1 ? " Italic" : "";
					const medium = tf.indexOf("Medium") !== -1;
					const demi = tf.indexOf("Demi") !== -1;
					const bold = tf.indexOf("Bold") !== -1;
					const weight = medium ? "Medium" : demi ? "Demi" : bold ? "Demi" : "Book";
					const font = `Franklin Gothic ${weight}${italic}`;

					this.map.setLayoutProperty(lid, "text-font", [font]);
				});
			});
		}

		if (!this.options.labels) this.hideLabels();
		if (this.options.hideBackgroundMap) this.hideBackgroundMap();

		// change the language of the map's labels
		this.setLanguage(getLanguage());

		// make the map noninteracive (we never want interactivity in the inital state
		this.setInteractivity(false);

		// but activate interactivity for certain interactions

		if (!this.options.noAutoInteractiveClick) {
			this.map.on("click", () => {
				this.setInteractivity(true);
			});
			this.map.on("mousedown", () => {
				this.setInteractivity(true);
			});
		}
		if (!this.options.noAutoInteractiveTouch) {
			this.map.on("touchstart", (e) => {
				if (!this.interactive && e.originalEvent.targetTouches.length === 2) {
					this.setInteractivity(true);
				}
			});
		}

		// go to the inital view as set in the options
		this.toInitialView(true);

		// keep view on resize, unless user has interacted already (don't annoy them)
		this.map.on("resize", () => {
			if (!this.userInteracted) this.toInitialView();
		});

		// detect user interaction
		"dragstart rotatestart boxzoomstart wheel".split(" ").forEach((e) => {
			this.map.once(e, () => {
				this.userInteracted = true;
			});
		});

		// track custom layers
		this.customLayers = [];

		// hide "duplicate layer id" errors - these happen very often
		// with maptiler styles and clutter the console
		this.map.on("error", (e) => {
			// console.log(e);
			if (e.error.message.indexOf("duplicate layer") !== -1) return;
			console.error(e.error);
		});
	}

	toInitialView(noAnimation, noPadding) {
		this.programmaticMove = true;

		const cam = this.map.cameraForBounds(this.options.initialBounds);
		if (cam) {
			if (this.options.initialMinZoom && cam.zoom < this.options.initialMinZoom) {
				cam.zoom = this.options.initialMinZoom;
				if (this.options.initialCenter) {
					cam.center = this.options.initialCenter;
				}
			}
			if (noPadding) {
				cam.padding = {
					left: 0,
					top: 0,
					bottom: 0,
					right: 0,
				};
			}
			cam.animate = !noAnimation;
			this.map.easeTo(cam);
			this._blockMoved = true;
			this.elems.main.classList.remove("tsg-map--moved");
		}
	}

	setInteractivity(interactive) {
		this.interactive = interactive;
		this.elems.main.classList.toggle("tsg-map--interactive", interactive);

		// these types of handlers are always disabled
		this.map.keyboard.disable();
		this.map.boxZoom.disable();

		// these will be enabled or disabled
		const handlers = [
			"scrollZoom",
			"dragPan",
			// 'dragRotate',
			"doubleClickZoom",
			"touchZoomRotate",
			// 'touchPitch',
		];

		handlers.forEach((h) => {
			this.map[h][interactive ? "enable" : "disable"]();
		});

		if (interactive) this.userInteracted = true;
	}

	hideLabels() {
		this.loaded.then(() => {
			this.labelLayerIds.forEach((l) => {
				this.map.setLayoutProperty(l, "visibility", "none");
			});
		});
	}

	showLabels() {
		this.loaded.then(() => {
			this.labelLayerIds.forEach((l) => {
				this.map.setLayoutProperty(l, "visibility", "visible");
			});
		});
	}

	hideBackgroundMap() {
		this.loaded.then(() => {
			const layers = this.map.getStyle().layers;

			layers.forEach((l) => {
				if (this.customLayers.indexOf(l.id) === -1) {
					this.map.setLayoutProperty(l.id, "visibility", "none");
				}
			});
		});
	}

	showBackgroundMap() {
		this.loaded.then(() => {
			const layers = this.map.getStyle().layers;

			layers.forEach((l) => {
				this.map.setLayoutProperty(l.id, "visibility", "visible");
			});
		});
	}

	hideTooltip() {
		this.elems.main.classList.add("tsg-map--hide-tooltip");
	}

	showTooltip() {
		this.elems.main.classList.remove("tsg-map--hide-tooltip");
	}

	setLanguage(l) {
		if (this.provider === "self") return;
		if (this.provider === "maptiler") this.map.setLanguage(l, true);
		if (this.provider === "mapbox") {
			(this.languagePlugin
				? Promise.resolve()
				: loadScript(`${PATH}vendor/mapbox-gl-language.js`).then(() => {
						this.languagePlugin = new window.MapboxLanguage({});
						this.map.addControl(this.languagePlugin);
				  })
			).then(() => {
				this.loaded.then(() => {
					this.map.setStyle(this.languagePlugin.setLanguage(this.map.getStyle(), l));
				});
			});
		}
	}

	setupPopup() {
		const opts = {
			variant: this.options.popupVariant,
			fixedBreak: this.options.popupFixedBreak,
			barMessage: this.options.popupBarMessage,
			barMessageClose: this.options.popupBarMessageClose,
			callbackClose: (isFixed) => {
				this.elems.main.classList.remove("tsg-map--popup-open");
				this.elems.main.classList.remove("tsg-map--popup-open-mobile");
				if (!isFixed && this.options.popupShift) {
					this.map.easeTo({
						padding: {
							left: 0,
							top: 0,
							right: 0,
							bottom: 0,
						},
					});
				}
				if (this.options.popupCallbackClose) {
					this.options.popupCallbackClose(this);
				}
			},
			callbackOpen: (fixed) => {
				this.elems.main.classList.add("tsg-map--popup-open");
				if (this.popup.isMobile) {
					this.elems.main.classList.add("tsg-map--popup-open-mobile");
				} else {
					this.elems.main.classList.remove("tsg-map--popup-open-mobile");
				}
				if (this.options.popupShift) {
					this.activated.then(() => {
						if (fixed) {
							this.map.setPadding(this.popup.getMapPadding());
							this.toInitialView();
						} else {
							this.map.easeTo({ padding: this.popup.getMapPadding() });
						}
					});
				}
				if (this.options.popupCallbackOpen) {
					this.options.popupCallbackOpen(this);
				}
			},
		};
		if (this.options.popupWidth) opts.width = this.options.popupWidth;
		this.popup = new MapPopup(this.elems.popupContainer, opts);
	}

	setupControls() {
		if (this.options.scale) {
			this.activated.then(() => {
				this.map.addControl(new mapboxgl.ScaleControl(), "bottom-right");
			});
		}

		if (this.options.controlsLeft) this.elems.controls.classList.add("tsg-map__controls--left");

		const addControl = (type, handler, text) => {
			const e = document.createElement("div");
			e.classList.add("tsg-map-control");
			e.classList.add(`tsg-map-control--${type}`);
			e.classList.add("tsg-font-sans");
			e.addEventListener("click", handler);
			if (text) {
				e.innerHTML = `<div class="tsg-map-control__texth">${text}</div><div class="tsg-map-control__text">${text}</div>`;
			}
			this.elems.controls.appendChild(e);
			return e;
		};

		this.controlZoomOut = addControl("zoom-out", () => {
			this._blockMoved = true;
			this.map.zoomTo(this.map.getZoom() - 0.7);
			this.setInteractivity(true);
		});
		this.controlZoomIn = addControl("zoom-in", () => {
			this._blockMoved = true;
			this.map.zoomTo(this.map.getZoom() + 0.7);
			this.setInteractivity(true);
		});
		this.controlReset = addControl("reset", () => {
			this.toInitialView();
		});

		this.activated.then(() => {
			this.map.on("zoomend", () => {
				if (!this._blockMoved) this.elems.main.classList.add("tsg-map--moved");
			});
			this.map.on("moveend", () => {
				if (!this._blockMoved) this.elems.main.classList.add("tsg-map--moved");
				this._blockMoved = false;
			});
		});

		this.controlActivate = addControl(
			"activate",
			() => {
				this.setInteractivity(true);
				this.map.zoomTo(this.map.getZoom() + 0.1);
			},
			"Zoomen aktivieren"
		);
		this.controlDeactivate = addControl(
			"deactivate",
			() => {
				this.setInteractivity(false);
				this.map.zoomTo(this.map.getZoom() - 0.1);
			},
			"Scrollen aktivieren"
		);
	}

	hideControls() {
		this.elems.main.classList.add("tsg-map--hide-controls");
	}

	showControls() {
		this.elems.main.classList.remove("tsg-map--hide-controls");
	}

	hideAttribution() {
		this.elems.main.classList.add("tsg-map--no-attribution");
		const attrib = document.querySelector(".mapboxgl-ctrl-attrib-inner");
		if (attrib) attrib.style.display = "none";
	}

	showAttribution() {
		this.elems.main.classList.remove("tsg-map--no-attribution");
		const attrib = document.querySelector(".mapboxgl-ctrl-attrib-inner");
		if (attrib) attrib.style.display = "block";
	}

	setupCircleMarkers(data, options) {
		this.circleOptions = options;

		this.activated.then(() => {
			const minSize = options.minCircleSize ? options.minCircleSize : 2;

			const maxValue = Math.max(...data.map((x) => x.value), 0);

			this.normalize = function (x, max) {
				const normval = max * Math.sqrt(x / maxValue);
				if (normval > minSize) {
					return Math.round(normval);
				}
				return minSize;
			};

			data.sort((a, b) => descending(a.value, b.value));
			this.selected = null;
			this.circles = {};
			this.circleBorders = {};
			this.circleBorderMarkers = {};
			this.circleInteractionOverlays = {};
			this.circleInteractionOverlayMarkers = {};
			this.circleMarkers = {};
			this.markerTexts = {};
			this.elTexts = {};
			this.elTextMarkers = {};
			this.labelOrder = data.map((d) => d.label);
			this.circleBorderColor = options.circleBorderColor;
			this.circleColor = options.circleColor;
			this.selectedColor = options.selectedColor;
			this.selectedBorderColor = options.selectedBorderColor;

			data.forEach((d) => {
				const diameter = Math.round(this.normalize(d.value, options.maxCircleSize)) + 0.5;

				const tinyThreshold = 25;
				const isTiny = diameter < tinyThreshold;

				const elCircle = document.createElement("div");
				elCircle.className = "tsg-marker-circle";
				elCircle.classList.toggle("tsg-marker-circle--hidden", d.hidden);
				elCircle.style.width = `${diameter}px`;
				elCircle.style.height = `${diameter}px`;
				elCircle.style.backgroundColor = this.circleColor;
				this.circles[d.label] = elCircle;
				this.circleMarkers[d.label] = new mapboxgl.Marker(elCircle).setLngLat([d.lon, d.lat]);

				const elCircleBorder = document.createElement("div");
				elCircleBorder.className = "tsg-marker-circle--border";
				elCircleBorder.classList.toggle("tsg-marker-circle--hidden", d.hidden);
				elCircleBorder.style.width = `${diameter}px`;
				elCircleBorder.style.height = `${diameter}px`;
				elCircleBorder.style.borderColor = this.circleBorderColor;
				this.circleBorders[d.label] = elCircleBorder;
				this.circleBorderMarkers[d.label] = new mapboxgl.Marker(elCircleBorder).setLngLat([d.lon, d.lat]);

				const elCircleInteractionOverlay = document.createElement("div");
				elCircleInteractionOverlay.className = "tsg-marker-circle--interaction_overlay";
				elCircleInteractionOverlay.classList.toggle("tsg-marker-circle--hidden", d.hidden);
				elCircleInteractionOverlay.style.width = `${isTiny ? tinyThreshold : diameter}px`;
				elCircleInteractionOverlay.style.height = `${isTiny ? tinyThreshold : diameter}px`;
				elCircleInteractionOverlay.style.opacity = 0;
				this.circleInteractionOverlays[d.label] = elCircleInteractionOverlay;
				this.circleInteractionOverlayMarkers[d.label] = new mapboxgl.Marker(elCircleInteractionOverlay).setLngLat([
					d.lon,
					d.lat,
				]);

				const elText = document.createElement("div");
				elText.className = "tsg-marker-circle tsg-marker-circle--text";
				elText.classList.toggle("tsg-marker-circle--hidden", d.hidden);
				elText.style.width = `${diameter}px`;
				elText.style.height = `${diameter}px`;
				this.elTexts[d.label] = elText;
				const markerText = document.createElement("div");
				markerText.className = "tsg-marker-text";
				markerText.innerHTML = `
          <span class="tsg-marker-text-label">${d.label}</span>
          <span class="tsg-marker-text-value">${
						d.verbose && options.formatFunctionVerbose
							? options.formatFunctionVerbose(d.value)
							: options.formatFunction(d.value)
					}</span>
        `;
				if (d.alignLabel) {
					markerText.classList.add(`tsg-marker-text--${d.alignLabel}`);
				}
				if (d.labelVisible) {
					markerText.classList.add(`tsg-marker-text--visible-${d.labelVisible}`);
				}
				if (d.labelSingleLine) {
					markerText.classList.add("tsg-marker-text--single-line");
				}
				this.markerTexts[d.label] = markerText;
				elText.appendChild(markerText);

				const click = (e) => {
					e.stopPropagation();

					if (options.clickCircleCallbackDataAttr) {
						if (this.selected === d[options.clickCircleCallbackDataAttr]) {
							this.circles[this.selected].classList.remove("tsg-marker-circle--highlight");
							this.markerTexts[this.selected].classList.remove("tsg-marker-text--highlight");
							this.circleBorders[this.selected].style.borderColor = options.circleBorderColor;
							this.circles[this.selected].style.backgroundColor = this.circleColor;
							this.selected = null;
							this.popup.close();
						} else {
							// console.log('select');
							if (this.selected) {
								this.circles[this.selected].classList.remove("tsg-marker-circle--highlight");
								this.markerTexts[this.selected].classList.remove("tsg-marker-text--highlight");
								this.circleBorders[this.selected].style.borderColor = this.circleBorderColor;
								this.circles[this.selected].style.backgroundColor = this.circleColor;
							}
							options.clickCircleCallback(d[options.clickCircleCallbackDataAttr]);
							this.selected = d[options.clickCircleCallbackDataAttr];
							this.circles[this.selected].className += " tsg-marker-circle--highlight";
							this.markerTexts[this.selected].className += " tsg-marker-text--highlight";
							this.circles[this.selected].style.backgroundColor = this.selectedColor;
							this.circleBorders[this.selected].style.borderColor = this.selectedBorderColor;
						}
					} else if (options.clickCircleCallback) {
						options.clickCircleCallback(d);
					}
				};

				const mouseenter = (e) => {
					e.stopPropagation();
					this.markerTexts[d.label].classList.add("tsg-marker-text--hovered");
				};

				const mouseover = (e) => {
					e.stopPropagation();

					if (options.hoverCircleCallback) {
						options.hoverCircleCallback(d);
					}
				};

				const mouseleave = (e) => {
					e.stopPropagation();
					this.markerTexts[d.label].classList.remove("tsg-marker-text--hovered");
				};

				elCircleInteractionOverlay.addEventListener("click", click);
				elText.addEventListener("click", click);

				elCircleInteractionOverlay.addEventListener("mouseenter", mouseenter);
				elText.addEventListener("mouseenter", mouseenter);

				elCircleInteractionOverlay.addEventListener("mouseover", mouseover);
				elText.addEventListener("mouseover", mouseover);

				elCircleInteractionOverlay.addEventListener("mouseleave", mouseleave);
				elText.addEventListener("mouseleave", mouseleave);

				this.elTextMarkers[d.label] = new mapboxgl.Marker(elText).setLngLat([d.lon, d.lat]);
			});

			this.labelOrder.forEach((d) => {
				this.circleMarkers[d].addTo(this.map);
			});
			this.labelOrder.forEach((d) => {
				this.circleBorderMarkers[d].addTo(this.map);
			});
			this.labelOrder.forEach((d) => {
				this.circleInteractionOverlayMarkers[d].addTo(this.map);
			});
			this.labelOrder.forEach((d) => {
				this.elTextMarkers[d].addTo(this.map);
			});

			if (options.minZoom || options.maxZoom) {
				this.map.on("zoom", () => {
					let hide = false;
					if (options.minZoom && this.map.getZoom() < options.minZoom) {
						hide = true;
					}
					if (options.maxZoom && this.map.getZoom() > options.maxZoom) {
						hide = true;
					}
					this.elems.main.classList.toggle("tsg-map--hide-circles", hide);
				});
			}
		});
	}

	updateCircles(data, refMaxCircleSize) {
		data.sort((a, b) => descending(a.value, b.value));
		data.forEach((d) => {
			const diameter = this.normalize(d.value, refMaxCircleSize) + 0.5;

			const tinyThreshold = 25;
			const isTiny = diameter < tinyThreshold;

			this.circles[d.label].style.width = `${diameter}px`;
			this.circles[d.label].style.height = `${diameter}px`;
			this.elTexts[d.label].style.width = `${diameter}px`;
			this.elTexts[d.label].style.height = `${diameter}px`;
			this.circleBorders[d.label].style.width = `${diameter}px`;
			this.circleBorders[d.label].style.height = `${diameter}px`;
			this.circleInteractionOverlays[d.label].style.width = `${isTiny ? tinyThreshold : diameter}px`;
			this.circleInteractionOverlays[d.label].style.height = `${isTiny ? tinyThreshold : diameter}px`;

			if (d.labelVisible) {
				this.markerTexts[d.label].classList.remove("tsg-marker-text--visible-never");
				this.markerTexts[d.label].classList.remove("tsg-marker-text--visible-hover");
				this.markerTexts[d.label].classList.remove("tsg-marker-text--visible-always");
				this.markerTexts[d.label].classList.add(`tsg-marker-text--visible-${d.labelVisible}`);
			}

			this.circles[d.label].classList.toggle("tsg-marker-circle--hidden", d.hidden);
			this.elTexts[d.label].classList.toggle("tsg-marker-circle--hidden", d.hidden);
			this.circleBorders[d.label].classList.toggle("tsg-marker-circle--hidden", d.hidden);
			this.circleInteractionOverlays[d.label].classList.toggle("tsg-marker-circle--hidden", d.hidden);
			this.markerTexts[d.label].getElementsByTagName("span")[0].innerHTML = d.label;

			this.markerTexts[d.label].getElementsByTagName("span")[1].innerHTML = this.circleOptions.formatFunction(d.value);
		});
	}

	onZoom(zoomCallback) {
		this.activated.then(() => {
			zoomCallback();
			this.map.on("zoom", () => {
				zoomCallback();
			});
		});
	}

	checkChoroplethFilter(choroplethId, feature) {
		let filtered = false;

		this.choropleths[choroplethId].options.filter.forEach((p) => {
			filtered = filtered || feature.properties[p.property] === p.value;
		});

		return filtered;
	}

	updateChoroplethOpts(choroplethId, opts) {
		this.choropleths[choroplethId].options = { ...this.choropleths[choroplethId].options, ...opts };
	}

	addChoropleth(geometry, opts) {
		const options = {
			id: `choropleth${Object.keys(this.choropleths).length}`,
			borderWidth: 0.0,
			borderOpacity: 1.0,
			opacity: 1.0,
			noDataColor: "#0F0",
			minzoom: 0,
			maxzoom: 24,
			...opts,
		};
		const choroplethId = options.id;
		this.choropleths[choroplethId] = {};
		this.choropleths[choroplethId].options = options;

		this.selectedChoroplethId = null;
		this.hoveredChoroplethId = null;

		this.activated
			.then(() => this.loaded)
			.then(() => {
				if (this.choropleths[choroplethId].options.geoType === "topojson") {
					geometry = convertToGeoJSON(geometry);
				}

				geometry.features.forEach((feature) => {
					feature.properties.id = feature.properties[this.choropleths[choroplethId].options.identifier];
				});

				this.choropleths[choroplethId].geometry = geometry;

				if (!options.interactive === false) {
					const mouseenter = (e) => {
						this.map.getCanvas().style.cursor = "pointer";
						if (e.features.length > 0) {
							const feature = e.features[0];
							// const filtered = this.checkChoroplethFilter(choroplethId, feature);
							const filtered = feature.properties.filtered;

							if (this.choropleths[choroplethId].options.mouseenterCallback) {
								this.choropleths[choroplethId].options.mouseenterCallback(feature, filtered);
								e.originalEvent.preventDefault();
							}
						}
					};
					const mouseleave = () => {
						this.map.getCanvas().style.cursor = "";
						if (this.choropleths[choroplethId].options.mouseleaveCallback) {
							this.choropleths[choroplethId].options.mouseleaveCallback();
						}
						this.hoverChoropleth(choroplethId, null);
					};

					const mousemove = (e) => {
						e.originalEvent.stopPropagation();
						if (e.features.length > 0) {
							const feature = e.features[0];
							// const filtered = this.checkChoroplethFilter(choroplethId, feature);
							const filtered = feature.properties.filtered;

							if (!filtered) {
								this.hoverChoropleth(choroplethId, feature.id);
							} else {
								this.hoverChoropleth(choroplethId, null);
							}

							if (this.choropleths[choroplethId].options.mousemoveCallback) {
								this.choropleths[choroplethId].options.mousemoveCallback(feature, filtered);
							}
						}
					};

					const choroplethClick = (e) => {
						e.preventDefault();

						if (e.features.length > 0) {
							const feature = e.features[0];
							// const filtered = this.checkChoroplethFilter(choroplethId, feature);
							const filtered = feature.properties.filtered;

							if (!filtered) {
								this.selectChoropleth(choroplethId, feature.id);
								this.setInteractivity(true);
							}

							if (this.choropleths[choroplethId].options.choroplethClickCallback) {
								this.choropleths[choroplethId].options.choroplethClickCallback(feature, filtered, e.lngLat);
							}
						}
					};

					const backgroundClick = (e) => {
						if (!e.defaultPrevented) {
							this.selectChoropleth(choroplethId, null);
							if (this.choropleths[choroplethId].options.backgroundClickCallback) {
								this.choropleths[choroplethId].options.backgroundClickCallback();
							}
						}
					};

					this.map.on("mousemove", choroplethId, mousemove);
					this.map.on("mouseenter", choroplethId, mouseenter);
					this.map.on("mouseleave", choroplethId, mouseleave);
					if (this.choropleths[choroplethId].options.choroplethClickCallback) {
						this.map.on("click", choroplethId, choroplethClick);
						this.map.on("click", backgroundClick);
					}
				}

				// create map source based on geo shapes and map layer based on the data for Mapbox
				const sourceOptions = {
					type: "geojson",
					data: geometry,
					promoteId: "id",
				};
				this.map.addSource(choroplethId, sourceOptions);

				let beforeLayer;
				if (this.choropleths[choroplethId].options.beforeLayer) {
					beforeLayer = this.choropleths[choroplethId].options.beforeLayer;
				}
				this.map.addLayer(
					{
						id: choroplethId,
						type: "fill",
						source: choroplethId,
						minzoom: this.choropleths[choroplethId].options.minzoom,
						maxzoom: this.choropleths[choroplethId].options.maxzoom,
						layout: {},
						paint: {
							"fill-opacity": this.choropleths[choroplethId].options.opacity,
						},
					},
					beforeLayer
				);
				this.customLayers.push(choroplethId);
				if (this.choropleths[choroplethId].options.noDataPattern) {
					const transparent =
						"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
					this.map.loadImage(transparent, (error, image) => {
						if (!this.map.hasImage("transparent")) this.map.addImage("transparent", image);
					});
					this.map.addLayer(
						{
							id: `${choroplethId}_pattern`,
							type: "fill",
							source: choroplethId,
							minzoom: this.choropleths[choroplethId].options.minzoom,
							maxzoom: this.choropleths[choroplethId].options.maxzoom,
							layout: {},
							paint: {
								"fill-opacity": this.choropleths[choroplethId].options.opacity,
							},
						},
						beforeLayer
					);
					this.customLayers.push(`${choroplethId}_pattern`);
				}

				// const borderColor = this.generateProperty(
				//   this.choropleths[choroplethId].options.borderColor,
				//   this.choropleths[choroplethId].options.selectedBorderColor,
				//   this.choropleths[choroplethId].options.hoveringBorderColor,
				// );

				// const borderWidth = this.generateProperty(
				//   this.choropleths[choroplethId].options.borderWidth,
				//   this.choropleths[choroplethId].options.selectedBorderWidth,
				//   this.choropleths[choroplethId].options.hoveringBorderWidth,
				// );

				const borderColor = this.generateProperty(
					this.choropleths[choroplethId].options.borderColor,
					null,
					null,
					this.choropleths[choroplethId].options.filteredBorderColor
				);

				const borderWidth = this.generateProperty(this.choropleths[choroplethId].options.borderWidth, null, null);

				this.map.addLayer(
					{
						id: `${choroplethId}_lines`,
						type: "line",
						source: choroplethId,
						minzoom: this.choropleths[choroplethId].options.minzoom,
						maxzoom: this.choropleths[choroplethId].options.maxzoom,
						paint: {
							"line-color": borderColor,
							"line-width": borderWidth,
							"line-blur": this.choropleths[choroplethId].options.lineBlur || 0,
							"line-opacity": this.choropleths[choroplethId].options.borderOpacity,
							"line-offset": [
								"case",
								["boolean", ["feature-state", "hover"], false],
								this.choropleths[choroplethId].options.selectedBorderWidth / 1.5,
								["boolean", ["feature-state", "click"], false],
								this.choropleths[choroplethId].options.selectedBorderWidth / 1.5,
								0,
							],
						},
						layout: {
							"line-cap": "round",
							"line-join": "round",
						},
					},
					beforeLayer
				);
				this.customLayers.push(`${choroplethId}_lines`);

				if (this.choropleths[choroplethId].options.additionalOutlines) {
					this.setAdditionalChoroplethGeometry(
						choroplethId,
						this.choropleths[choroplethId].options.additionalOutlines,
						beforeLayer
					);
				}

				if (this.choropleths[choroplethId].options.customLabels) {
					this.choropleths[choroplethId].options.customLabels.forEach((ls) => {
						this.addLabels(
							ls[1],
							ls[0],
							this.choropleths[choroplethId].options.customLabelsOffset || [0, 0],
							this.choropleths[choroplethId].options.customLabelFontSize || 14
						);
					});
				}
				if (this.choropleths[choroplethId].options.data) {
					this.setChoroplethData(choroplethId, this.choropleths[choroplethId].options.data);
				}
			});
	}

	setAdditionalChoroplethGeometry(choroplethId, addGeometry, beforeLayer) {
		beforeLayer = beforeLayer || this.choropleths[choroplethId].options.beforeLayer;

		addGeometry.forEach((outlines) => {
			const id = `${choroplethId}_add_${outlines.id}`;

			this.removeGeometry(id);

			this.addGeometry(outlines.data, {
				id,
				type: "line",
				geoType: outlines.geoType,
				borderColor: outlines.color,
				borderWidth: outlines.width,
				beforeLayer,
			});
		});
	}

	generateProperty(original, selected, hovering, filtered) {
		let property = original;

		const clickCondition = ["boolean", ["feature-state", "click"], false];
		const hoverCondition = ["boolean", ["feature-state", "hover"], false];
		const filterCondition = ["boolean", ["get", "filtered"], true];

		if (selected || hovering || filtered) {
			property = ["case"];

			if (selected) {
				property.push(clickCondition, selected);
			}
			if (hovering) {
				property.push(hoverCondition, hovering);
			}
			if (filtered) {
				property.push(filterCondition, filtered);
			}
			property.push(original);
		}

		return property;
	}

	selectChoropleth(choroplethId, newId, stayWhereYouAre) {
		const oldId = this.selectedChoroplethId;

		if (this.map.getLayer(`${choroplethId}-selected`)) {
			this.map.removeLayer(`${choroplethId}-selected`);
		}
		if (this.map.getSource(`${choroplethId}-selected`)) {
			this.map.removeSource(`${choroplethId}-selected`);
		}

		// deselect
		if (oldId) {
			this.selectedChoroplethId = null;
			if (this.choropleths[choroplethId].options.zoomOnClick && !stayWhereYouAre) {
				this.toInitialView();
			}
		}

		// select
		if (!oldId || oldId !== newId) {
			this.selectedChoroplethId = newId;

			const orgFeature = this.choropleths[choroplethId].geometry.features.filter((f) => f.properties.id === newId)[0];

			if (orgFeature) {
				this.map.addSource(`${choroplethId}-selected`, {
					type: "geojson",
					data: orgFeature,
				});

				this.map.addLayer(
					{
						id: `${choroplethId}-selected`,
						type: "line",
						source: `${choroplethId}-selected`,
						layout: {},
						paint: {
							"line-color": this.choropleths[choroplethId].options.selectedBorderColor,
							"line-width": this.choropleths[choroplethId].options.selectedBorderWidth,
						},
					},
					this.firstLabelLayer
				);
				this.customLayers.push(`${choroplethId}-selected`);
			}

			if (this.choropleths[choroplethId].options.zoomOnClick) {
				if (orgFeature && !stayWhereYouAre) {
					const zoom = Math.max(this.map.getZoom(), this.choropleths[choroplethId].options.zoomLevel || 7);

					const pad = this.choropleths[choroplethId].options.zoomPadding;
					this.zoomToFeature(orgFeature, zoom, typeof pad === "function" ? pad() : pad);
				}
			}
		}
	}

	zoomToFeature(feature, zoom = null, pad = null) {
		const coordinates = feature.geometry.coordinates[0];
		const isNested = Array.isArray(coordinates[0][0]);

		const bounds = new mapboxgl.LngLatBounds();

		coordinates.forEach((coord) => {
			if (isNested) {
				coord.forEach((nestedCoord) => {
					const tempCoord = new mapboxgl.LngLat(nestedCoord[0], nestedCoord[1]);
					bounds.extend(tempCoord);
				});
			} else {
				const tempCoord = new mapboxgl.LngLat(coord[0], coord[1]);
				bounds.extend(tempCoord);
			}
		});

		this.programmaticMove = true;
		const options = zoom ? { zoom } : {};

		if (pad) {
			options.padding = pad;
		}
		this.map.fitBounds(bounds, options);
	}

	hoverChoropleth(choroplethId, newId) {
		if (this.hoveredChoroplethId === newId) return;

		const hovering = newId !== null;

		let hoverSource = this.map.getSource(`${choroplethId}-hovering`);

		const emptyGeojson = {
			type: "FeatureCollection",
			features: [],
		};

		if (!hoverSource) {
			this.map.addSource(`${choroplethId}-hovering`, {
				type: "geojson",
				data: emptyGeojson,
			});

			this.map.addLayer(
				{
					id: `${choroplethId}-hovering`,
					type: "line",
					source: `${choroplethId}-hovering`,
					layout: {},
					paint: {
						"line-color": this.choropleths[choroplethId].options.hoveringBorderColor,
						"line-width": this.choropleths[choroplethId].options.hoveringBorderWidth,
					},
				},
				this.firstLabelLayer
			);
			this.customLayers.push(`${choroplethId}-hovering`);

			hoverSource = this.map.getSource(`${choroplethId}-hovering`);
		} else {
			this.map.setPaintProperty(
				`${choroplethId}-hovering`,
				"line-width",
				this.choropleths[choroplethId].options.hoveringBorderWidth
			);
		}

		if (hovering) {
			const orgFeature = this.choropleths[choroplethId].geometry.features.filter((f) => f.properties.id === newId)[0];
			hoverSource.setData(orgFeature);
		} else {
			hoverSource.setData(emptyGeojson);
		}

		this.hoveredChoroplethId = newId;
	}

	setChoroplethGeometry(choroplethId, geometry, data, opts) {
		this.choropleths[choroplethId].options = {
			...this.choropleths[choroplethId].options,
			...opts,
		};
		if (!this.map.getSource(choroplethId)) return;

		if (this.choropleths[choroplethId].options.geoType === "topojson") {
			geometry = convertToGeoJSON(geometry);
		}

		this.choropleths[choroplethId].geometry = geometry;
		this.map.getSource(choroplethId).setData(geometry);

		this.setChoroplethData(choroplethId, data);
	}

	setChoroplethData(choroplethId, data, opts) {
		// TODO for some time in the future:
		// (https://docs.mapbox.com/mapbox-gl-js/example/data-join/) und hier (https://bl.ocks.org/danvk/210d79658597630225663f8a9a2f73f9)
		// ==> wenn sich die geometrie nicht verändert eine crazy mapbox expression mit den ganzen farben generieren und setPaintProperty benutzen
		// bei der gelegenheit auch alles auf den `color` modus umstellen
		this.choropleths[choroplethId].options = {
			...this.choropleths[choroplethId].options,
			...opts,
		};

		// calculate min and max values for numerical data if not specified
		if (this.choropleths[choroplethId].options.dataType === "numerical") {
			if (this.choropleths[choroplethId].options.customMinValue === undefined) {
				this.choropleths[choroplethId].options.minValue = Math.min(...Object.values(data));
			} else {
				this.choropleths[choroplethId].options.minValue = this.choropleths[choroplethId].options.customMinValue;
			}

			if (this.choropleths[choroplethId].options.customMaxValue === undefined) {
				this.choropleths[choroplethId].options.maxValue = Math.max(...Object.values(data));
			} else {
				this.choropleths[choroplethId].options.maxValue = this.choropleths[choroplethId].options.customMaxValue;
			}
		}

		this.activated
			.then(() => this.loaded)
			.then(() => {
				const source = this.map.getSource(choroplethId);
				const sourceData = source._data;

				// store data within the geo data
				sourceData.features.forEach((d) => {
					const id = d.properties[this.choropleths[choroplethId].options.identifier];
					d.properties.id = id;
					// const id = d.properties.id;

					if (this.choropleths[choroplethId].options.dataType === "colors") {
						d.properties.color = this.choropleths[choroplethId].options.colors[id];
					}
					if (this.choropleths[choroplethId].options.dataType === "colorScale") {
						const { colorScale, colorScaleValue } = this.choropleths[choroplethId].options;
						d.properties.color = colorScale(+d.properties[colorScaleValue]);
					}

					d.properties.data = data[id];
				});

				this.setChoroplethColors(choroplethId);
				source.setData(sourceData);

				const check = (e) => {
					if (e.isSourceLoaded) {
						this.dispatch("choroplethupdate");
						this.map.off("sourcedata", check);
					}
				};
				this.map.on("sourcedata", check);
			});
	}

	setChoroplethColors(choroplethId, colors) {
		this.choropleths[choroplethId].options.colors = {
			...this.choropleths[choroplethId].options.colors,
			...colors,
		};

		let choroplethColor = [];

		if (this.choropleths[choroplethId].options.dataType === "numerical") {
			// choroplethColor = [
			//   'interpolate',
			//   ['linear'],
			//   ['get', 'data'],
			//   this.choropleths[choroplethId].options.minValue,
			//   this.choropleths[choroplethId].options.colors.min,
			//   this.choropleths[choroplethId].options.maxValue,
			//   this.choropleths[choroplethId].options.colors.max,
			// ];
			choroplethColor = [
				"case",
				["==", ["typeof", ["get", "data"]], "number"], // check if data is not null
				[
					"interpolate",
					["linear"],
					["get", "data"],
					this.choropleths[choroplethId].options.minValue,
					this.choropleths[choroplethId].options.colors.min,
					this.choropleths[choroplethId].options.maxValue,
					this.choropleths[choroplethId].options.colors.max,
				],
				this.choropleths[choroplethId].options.noDataColor,
			];
		}

		if (this.choropleths[choroplethId].options.dataType === "categorical") {
			choroplethColor = [
				"match",
				["get", "data"],
				...Object.entries(this.choropleths[choroplethId].options.colors).flat(),
				this.choropleths[choroplethId].options.noDataColor, // other
			];
		}

		if (this.choropleths[choroplethId].options.dataType === "colors") {
			// choroplethColor = ['get', 'color'];
			choroplethColor = [
				"case",
				["==", ["typeof", ["get", "color"]], "string"], // check if color is not null
				["get", "color"],
				this.choropleths[choroplethId].options.noDataColor,
			];
		}

		if (this.choropleths[choroplethId].options.dataType === "colorScale") {
			// choroplethColor = ['get', 'color'];
			choroplethColor = [
				"case",
				["==", ["typeof", ["get", "color"]], "string"], // check if color is not null
				["get", "color"],
				this.choropleths[choroplethId].options.noDataColor,
			];
		}

		choroplethColor = this.generateProperty(
			choroplethColor,
			this.choropleths[choroplethId].options.selectedColor,
			this.choropleths[choroplethId].options.hoveringColor,
			this.choropleths[choroplethId].options.filteredColor
		);
		this.map.setPaintProperty(choroplethId, "fill-color", choroplethColor);

		if (this.choropleths[choroplethId].options.noDataPattern) {
			if (this.choropleths[choroplethId].options.noDataPatternTie) {
				// choroplethColor = ['get', 'color'];
				const choroplethPattern = [
					"case",
					["==", ["get", "filtered"], true],
					"transparent",
					["==", ["typeof", ["get", "color"]], "string"], // check if color is not null
					[
						"case",
						["==", ["get", "color"], this.choropleths[choroplethId].options.noDataPatternTieReplaceColor], // check if color is not null
						this.choropleths[choroplethId].options.noDataPatternTie,
						"transparent",
					],
					this.choropleths[choroplethId].options.noDataPattern,
				];
				this.map.setPaintProperty(`${choroplethId}_pattern`, "fill-pattern", choroplethPattern);
			} else {
				// choroplethColor = ['get', 'color'];
				const choroplethPattern = [
					"case",
					["==", ["get", "filtered"], true],
					"transparent",
					["==", ["typeof", ["get", "color"]], "string"], // check if color is not null
					"transparent",
					this.choropleths[choroplethId].options.noDataPattern,
				];
				this.map.setPaintProperty(`${choroplethId}_pattern`, "fill-pattern", choroplethPattern);
			}
		}
	}

	showChoropleth(choroplethId, show) {
		if (show) {
			this.showLayer(choroplethId);
			this.showLayer(choroplethId + "_lines");
			this.showLayer(choroplethId + "-hovering");
		} else {
			this.hideLayer(choroplethId);
			this.hideLayer(choroplethId + "_lines");
			this.hideLayer(choroplethId + "-hovering");
		}
	}

	createLabelsGeo(labels, offsetv, iconImage) {
		// Create a geojson for the labels
		const geojson = {
			type: "FeatureCollection",
			features: [],
		};

		labels.forEach((label) => {
			let anchor = "center";
			let offset = [0, 0];

			if (label.position === "bottom" || label.icon || iconImage) {
				anchor = "top";
				offset = [0, offsetv * 1.2];
			}

			if (label.position === "top") {
				anchor = "bottom";
				offset = [0, -offsetv * 0.8];
			}

			if (label.position === "left") {
				anchor = "right";
				offset = [-offsetv, 0.1];
			}

			if (label.position === "right") {
				anchor = "left";
				offset = [offsetv, 0.1];
			}

			const feature = {
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: label.lnglat,
				},
				properties: {
					text: label.name,
					icon: label.icon || iconImage || "",
					opacity: label.opacity === "undefined" ? 1 : label.opacity,
					anchor,
					offset,
				},
			};
			geojson.features.push(feature);
		});

		return geojson;
	}

	addLabels(labels, opts) {
		this.activated
			.then(() => this.loaded)
			.then(() => {
				const options = {
					minZoom: 0,
					offsetv: 0,
					fontSize: 14,
					color: "#000000",
					halo: true,
					haloWidth: 2,
					iconImage: "",
					haloColor: "#ffffff",
					textAllowOverlap: false,
					textAnchor: "center",
					...opts,
				};

				const id = options.id || `custom_labels_${options.minZoom}`;

				this.labels[id] = {
					id,
					labels,
					...options,
				};

				const geojson = this.createLabelsGeo(labels, this.labels[id].offsetv, this.labels[id].iconImage);

				// const style = this.map.getStyle();
				// style.glyphs = 'http://fonts.openmaptiles.org/{fontstack}/{range}.pbf';
				// this.map.setStyle(style);

				this.loaded.then(() => {
					this.map.addSource(id, {
						type: "geojson",
						data: geojson,
					});

					this.map.addLayer({
						id,
						type: "symbol",
						minzoom: this.labels[id].minZoom,
						maxzoom: this.labels[id].maxZoom || 22,
						source: id,
						layout: {
							"text-field": ["get", "text"],
							"text-anchor": ["get", "anchor"],
							"text-offset": ["get", "offset"],
							"text-size": this.labels[id].fontSize,
							"text-font": [
								this.labels[id].font
									? this.labels[id].font
									: this.options.useFranklin
									? "Franklin Gothic Book"
									: "PT Sans Regular",
							],
							"text-letter-spacing": this.labels[id].letterSpacing,
							"icon-image": ["get", "icon"],
							"text-allow-overlap": this.labels[id].textAllowOverlap,
							"text-transform": this.labels[id].textTransform || "none",
							"text-anchor": this.labels[id].textAnchor,
							// 'icon-ignore-placement': true,
						},
						paint: {
							"text-opacity": ["get", "opacity"],
							"text-color": this.labels[id].color,
							"text-halo-color": this.labels[id].haloColor,
							"text-halo-width": this.labels[id].halo ? this.labels[id].haloWidth : 0,
						},
					});
				});
				this.customLayers.push(id);
			});
	}

	updateLabels(labels, labelId) {
		const id = labelId;
		const options = this.labels[id];

		this.loaded.then(() => {
			const source = this.map.getSource(id);
			const geojson = this.createLabelsGeo(labels, options.offsetv, options.iconImage);
			if (source) source.setData(geojson);
		});
	}

	addGeometry(geometry, opts) {
		if (opts.geoType === "topojson") {
			geometry = convertToGeoJSON(geometry);
		}

		this.loaded.then(() => {
			this.map.addSource(opts.id, {
				type: "geojson",
				data: geometry,
			});

			this.map.addLayer(
				{
					id: opts.id,
					type: opts.type,
					source: opts.id,
					layout: {},
					paint: {
						"line-color": opts.borderColor,
						"line-width": opts.borderWidth,
					},
				},
				opts.beforeLayer
			);
			this.customLayers.push(opts.id);
		});
	}

	removeGeometry(geometryId) {
		this.loaded.then(() => {
			if (this.map.getLayer(geometryId)) this.map.removeLayer(geometryId);
			if (this.map.getSource(geometryId)) this.map.removeSource(geometryId);

			this.customLayers = this.customLayers.filter((l) => l !== geometryId);
		});
	}

	setupLines(geometry, opts) {
		this.activated
			.then(() => this.loaded)
			.then(() => {
				const options = {
					id: `lines${Object.keys(this.choropleths).length}`,
					color: "#0F0",
					width: 1.0,
					opacity: 1.0,
					hidden: [],
					dashed: false,
					...opts,
				};
				const linesId = options.id;
				this.lines[linesId] = {};
				this.lines[linesId].options = options;

				this.setLines(geometry, linesId);

				const color = [
					"case",
					["==", ["typeof", ["get", "color"]], "string"], // check if color is not null
					["get", "color"],
					this.lines[linesId].options.color,
				];
				let hoveredStateId = null;

				this.map.on("mousemove", linesId, (e) => {
					if (e.features.length > 0) {
						// const f = e.features[0];
						if (hoveredStateId !== null) {
							this.map.setFeatureState({ source: linesId, id: hoveredStateId }, { hover: false });
						}
						hoveredStateId = e.features[0].id;
						this.map.setFeatureState({ source: linesId, id: hoveredStateId }, { hover: true });
					}
				});

				// const color = [
				//   'case',
				//   ['boolean', ['feature-state', 'hover'], false],
				//   '#F00',
				//   '#00F',
				// ];

				const width = [
					"case",
					["==", ["typeof", ["get", "width"]], "number"], // check if width is not null
					["get", "width"],
					this.lines[linesId].options.width,
				];

				let beforeLayer;
				if (this.lines[linesId].options.beforeLayer) {
					beforeLayer = this.lines[linesId].options.beforeLayer;
				}

				this.map.addLayer(
					{
						id: linesId,
						type: "line",
						source: linesId,
						layout: {
							"line-cap": "round",
							"line-join": "round",
						},
						paint: {
							"line-color": color,
							"line-width": width,
							"line-opacity": this.lines[linesId].options.opacity,
							"line-dasharray": this.lines[linesId].options.dashed ? [1, 2] : [1],
						},
						filter: ["==", "hidden", false],
					},
					beforeLayer
				);
				this.customLayers.push(linesId);
			});
	}

	setLines(geometry, linesId) {
		// this.lines[linesId].options = {
		//   ...this.lines[linesId].options,
		//   ...opts,
		// };

		if (this.lines[linesId].options.geoType === "topojson") {
			geometry = convertToGeoJSON(geometry);
		}

		geometry.features.forEach((f) => {
			const id = f.properties.id;
			f.properties.hidden = this.lines[linesId].options.hidden.includes(id);
			// f.properties.hidden = false;
			if (this.lines[linesId].options.colors) {
				f.properties.color = this.lines[linesId].options.colors[id];
			}
			if (this.lines[linesId].options.widths) {
				f.properties.width = this.lines[linesId].options.widths[id];
			}
		});

		this.linesGeometry = geometry;

		const source = this.map.getSource(linesId);
		if (source) {
			source.setData(geometry);
		} else {
			this.map.addSource(linesId, {
				type: "geojson",
				data: geometry,
				promoteId: "id",
			});
		}
	}

	addPoints(data, opts) {
		this.activated
			.then(() => this.loaded)
			.then(() => {
				const options = {
					labelsFont: "PT Sans Regular",
					circlePaint: {
						"circle-radius": 4,
						"circle-color": "#fff",
						"circle-stroke-color": "#2a2a2a",
						"circle-stroke-width": 1,
					},
					...opts,
				};
				this.pointOptions = options;

				const geojson = {
					type: "FeatureCollection",
					features: data.map((d, i) => ({
						id: d.id || i,
						type: "Feature",
						properties: d,
						geometry: {
							type: "Point",
							coordinates: [d.lon || d.long || d.lng || d.longitude, d.lat || d.latitude],
						},
					})),
				};
				const sourceOptions = {
					type: "geojson",
					data: geojson,
				};

				if (options.cluster) {
					sourceOptions.cluster = true;
				}
				let lastLayer;
				if (options.belowLabels) lastLayer = this.firstLabelLayer;
				this.map.addSource(this.pointOptions.id, sourceOptions);

				if (options.cluster) {
					this.map.addLayer(
						{
							id: this.pointOptions.id,
							type: "circle",
							source: this.pointOptions.id,
							filter: ["has", "point_count"],
							paint: options.clusterPaint,
						},
						lastLayer
					);
					if (options.belowLabels) {
						lastLayer = `${this.pointOptions.id}clusters`;
					}
					this.customLayers.push(`${this.pointOptions.id}clusters`);

					this.map.addLayer(
						{
							id: this.pointOptions.id,
							type: "symbol",
							source: this.pointOptions.id,
							filter: ["has", "point_count"],
							layout: {
								"text-field": "{point_count_abbreviated}",
								"text-size": 12,
								"text-font": [this.options.useFranklin ? "Franklin Gothic Book" : this.pointOptions.labelsFont],
							},
						},
						lastLayer
					);
					if (options.belowLabels) lastLayer = this.pointOptions.id;
					this.customLayers.push(this.pointOptions.id);

					this.map.on("click", `${this.pointOptions.id}clusters`, (e) => {
						const features = this.map.queryRenderedFeatures(e.point, {
							layers: [`${this.pointOptions.id}clusters`],
						});
						const clusterId = features[0].properties.cluster_id;
						this.map.getSource(this.pointOptions.id).getClusterExpansionZoom(clusterId, (err, zoom) => {
							if (err) return;

							this.map.easeTo({
								center: features[0].geometry.coordinates,
								zoom,
							});
						});
					});
				}

				const layerOptionsPoints = {
					id: this.pointOptions.id,
					type: "circle",
					source: this.pointOptions.id,
					paint: options.circlePaint,
					layout: {
						...(options.sortKeyCircles
							? {
									"circle-sort-key": ["*", -1, ["number", ["get", options.sortKeyCircles], 0]],
							  }
							: {}),
					},
					...(options.minZoom ? { minzoom: options.minZoom } : {}),
					...(options.maxZoom ? { maxzoom: options.maxZoom } : {}),
				};
				if (options.cluster) {
					layerOptionsPoints.filter = ["!", ["has", "point_count"]];
				}
				this.map.addLayer(layerOptionsPoints, lastLayer);
				this.customLayers.push(this.pointOptions.id);
				if (options.belowLabels) lastLayer = this.pointOptions.id;

				let labelKey = null;
				if (Object.keys(data[0]).indexOf("title") !== -1) labelKey = "title";
				if (Object.keys(data[0]).indexOf("label") !== -1) labelKey = "label";
				if (options.labelKey) labelKey = options.labelKey;

				let sortKey = null;
				if (Object.keys(data[0]).indexOf("sort") !== -1) sortKey = "sort";
				if (options.sortKey) sortKey = options.sortKey;

				if (options.labels && labelKey) {
					const layerOptionsLabels = {
						id: `${this.pointOptions.id}labels`,
						type: "symbol",
						source: this.pointOptions.id,
						minzoom: options.labelMinZoom || 12,
						layout: {
							...(sortKey ? { "symbol-sort-key": ["to-number", ["get", sortKey]] } : {}),
							"text-anchor": "bottom",
							"text-field": `{${labelKey}}`,
							"text-font": [this.options.useFranklin ? "Franklin Gothic Book" : this.pointOptions.labelsFont],
							"text-size": 12,
							"text-radial-offset": options.labelOffset || -1,
							// 'symbol-sort-key': ['*', -1, ['number', ['get', options.priority], 0]],
						},
						// symbol-sort-key: ['*', -1, ['number', ['get', options.priority], 0]],
						paint: {
							"text-color": "#000000",
							"text-halo-color": "#ffffff",
							"text-halo-width": 2,
						},
					};
					if (options.cluster) {
						layerOptionsLabels.filter = ["!", ["has", "point_count"]];
					}
					this.map.addLayer(layerOptionsLabels, lastLayer);
					this.customLayers.push(`${this.pointOptions.id}labels`);
					if (options.belowLabels) lastLayer = `${this.pointOptions.id}labels`;
				}

				if (options.tooltips) {
					this.elems.main.classList.add("tsg-map--tooltips");
				}
				if (options.tooltipsOnlyDesktop) {
					this.elems.main.classList.add("tsg-map--tooltips-only-desktop");
				}

				if (this.popup) {
					this.popup.on("close", () => {
						this.resetPoints();
					});

					this.map.on("click", () => {
						this.popup.close();
					});
				}

				this.map.on("click", (e) => {
					const features = this.map.queryRenderedFeatures(e.point, {
						layers: [this.pointOptions.id, `${this.pointOptions.id}labels`],
					});
					const feature = features[0];

					if (feature) {
						if (!feature.id) feature.id = feature.properties.id;
						e.originalEvent.stopPropagation();
						this.selectPoint(
							feature,
							this.map.getZoom() < (options.selectMinZoom || 14) ? options.selectMinZoom || 14 : null
						);
					}
				});

				const mouseenter = (e) => {
					this.map.getCanvas().style.cursor = "pointer";
					if (options.mouseenterCallback) {
						options.mouseenterCallback(e.features[0]);
					}
				};
				const mouseleave = () => {
					this.map.getCanvas().style.cursor = "";
					if (options.mouseleaveCallback) {
						options.mouseleaveCallback();
					}
				};

				this.map.on("mouseenter", this.pointOptions.id, mouseenter);
				this.map.on("mouseleave", this.pointOptions.id, mouseleave);
				this.map.on("mouseenter", `${this.pointOptions.id}labels`, mouseenter);
				this.map.on("mouseleave", `${this.pointOptions.id}labels`, mouseleave);

				this.map.once("load", () => {
					this.dispatch("pointsloaded");
				});

				if (options.tooltips) {
					this.tooltip = new mapboxgl.Popup({
						closeButton: false,
						closeOnClick: false,
						offset: 10,
					});

					// prevent blurry text in chrome
					this.tooltip.on("open", () => {
						const e = this.tooltip._container;
						e.style.width = null;
						const w = 2 * Math.ceil(e.getBoundingClientRect().width / 2);
						e.style.width = `${w}px`;

						this.tooltip._content.addEventListener("click", () => {
							this.resetPoints();
						});
					});

					const mouseenterTt = (e) => {
						if (!this.currentFeatureId) {
							const coordinates = e.features[0].geometry.coordinates;
							this.tooltip
								.setLngLat(coordinates)
								.setHTML(options.popupContentFunction(e.features[0].properties))
								.addTo(this.map);
						}
					};

					const mouseleaveTt = () => {
						if (!this.currentFeatureId) {
							this.tooltip.remove();
						}
					};

					this.map.on("mouseenter", this.pointOptions.id, mouseenterTt);
					this.map.on("mouseleave", this.pointOptions.id, mouseleaveTt);

					this.map.on("zoom", () => {
						if (this.tooltip && this.currentFeatureId) {
							let f = this.map
								.queryRenderedFeatures({ layers: [this.pointOptions.id] })
								.filter((x) => x.id === this.currentFeatureId);
							if (f.length) {
								f = f[0];
								this.tooltip.setLngLat(f.geometry.coordinates);
							}
						}
					});
				}
			});
	}

	getPointMinMaxValues(key, minValue = 1) {
		const vs = this.map
			.queryRenderedFeatures({ layers: [this.pointOptions.id] })
			.map((x) => x.properties[key])
			.filter((x) => Number.isFinite(x));
		if (vs.length === 0) return [];
		const min = Math.min(...vs) || minValue;
		const max = Math.max(...vs);
		if (min === max) return [min];
		return [max, min];
	}

	resetPoints() {
		if (this.currentFeatureId) {
			this.map.setFeatureState(
				{
					source: this.pointOptions.id,
					id: this.currentFeatureId,
				},
				{
					current: false,
				}
			);
			this.currentFeatureId = null;
			this.pointOptions.onReset();

			if (this.tooltip) this.tooltip.remove();
		}
	}

	getFeatureFromLatLon(lat, lon) {
		const point = this.map.project([lon, lat]);
		const features = this.map.queryRenderedFeatures(point, {
			layers: [this.pointOptions.id],
		});
		return features;
	}

	selectPoint(feature, stayWhereYouAre = false, zoom = "default") {
		const p = feature.geometry ? feature.properties : feature;
		this.resetPoints();
		this.currentFeatureId = feature.id;
		this.pointOptions.onSelect(feature);
		this.map.setFeatureState(
			{
				source: this.pointOptions.id,
				id: this.currentFeatureId,
			},
			{
				current: true,
			}
		);
		if (this.popup) {
			this.popup.setContent(this.pointOptions.popupContentFunction(p));
			this.popup.open();
		}
		this.setInteractivity(true);
		const to = {
			center: feature.geometry ? feature.geometry.coordinates : [feature.lon, feature.lat],
			padding: this.popup?.getMapPadding() || 0,
		};
		if (zoom !== "default") to.zoom = zoom;
		this.map.stop();
		setTimeout(() => {
			if (!stayWhereYouAre) this.map.flyTo(to);
		});
		if (this.pointOptions.tooltips) {
			this.tooltip
				.setLngLat(feature.geometry ? feature.geometry.coordinates : [feature.lon, feature.lat])
				.setHTML(this.pointOptions.popupContentFunction(p))
				.addTo(this.map);
		}
	}

	hideLayer(geometryId) {
		this.loaded.then(() => {
			if (this.map.getLayer(geometryId)) this.map.setLayoutProperty(geometryId, "visibility", "none");
		});
	}

	showLayer(geometryId) {
		this.loaded.then(() => {
			if (this.map.getLayer(geometryId)) this.map.setLayoutProperty(geometryId, "visibility", "visible");
		});
	}
}

export default Map;
